"use client"

import { useCallback, useEffect, useReducer, useRef } from "react"
import { autosaveReducer, decideCommit, makeInitialState, type AutosaveState } from "./core"

export interface UseAutosaveFieldOptions<T> {
  initial: T
  /** Persiste el valor. DEBE lanzar (throw) si falla; el hook lo captura y reporta. */
  persist: (value: T) => Promise<void>
  /** Mensaje de error que bloquea el guardado, o null si es válido. */
  validate?: (value: T) => string | null
  /** "blur": persiste en commit()/onBlur() (texto/número). "immediate": persiste en setValue() (select/toggle). */
  mode?: "blur" | "immediate"
  equals?: (a: T, b: T) => boolean
  /** Cuánto dura "Guardado" antes de volver a idle. Default 1500ms. */
  savedLingerMs?: number
  /** Se llama con el mensaje en fallo de validación/persistencia (p.ej. un toast). */
  onError?: (message: string) => void
}

export interface AutosaveFieldApi<T> {
  value: T
  status: AutosaveState<T>["status"]
  error: string | null
  setValue: (value: T) => void
  commit: () => void
  onBlur: () => void
  /** Set + persist inmediato de un valor dado (grupos heterogéneos: toggle/select). */
  saveNow: (value: T) => void
  /** Re-sincroniza a un valor recién cargado del server (tras load async). */
  reset: (value: T) => void
}

const DEFAULT_LINGER = 1500

export function useAutosaveField<T>(opts: UseAutosaveFieldOptions<T>): AutosaveFieldApi<T> {
  const { mode = "blur", savedLingerMs = DEFAULT_LINGER } = opts

  const [state, dispatch] = useReducer(
    autosaveReducer as (s: AutosaveState<T>, a: Parameters<typeof autosaveReducer<T>>[1]) => AutosaveState<T>,
    opts.initial,
    makeInitialState,
  )

  // Refs para callbacks estables que no capturan closures viejos.
  const persistRef = useRef(opts.persist)
  const validateRef = useRef(opts.validate)
  const equalsRef = useRef(opts.equals)
  const onErrorRef = useRef(opts.onError)
  const stateRef = useRef(state)
  // Id monotónico: un persist que resuelve tarde no pisa un estado más nuevo.
  const reqIdRef = useRef(0)

  // Sincronizar refs DESPUÉS del render (no escribir refs durante el render —
  // regla react-hooks/refs). Corre en cada render → siempre apuntan a lo último.
  useEffect(() => {
    persistRef.current = opts.persist
    validateRef.current = opts.validate
    equalsRef.current = opts.equals
    onErrorRef.current = opts.onError
    stateRef.current = state
  })

  const commitValue = useCallback(
    (next: T) => {
      const { committed } = stateRef.current
      const d = decideCommit(next, committed, validateRef.current, equalsRef.current)
      if (d.unchanged) return
      if (d.validationError) {
        dispatch({ type: "VALIDATE_FAIL", message: d.validationError })
        onErrorRef.current?.(d.validationError)
        return
      }
      const id = ++reqIdRef.current
      dispatch({ type: "COMMIT_START" })
      persistRef.current(next).then(
        () => {
          if (id !== reqIdRef.current) return
          dispatch({ type: "COMMIT_OK", value: next })
        },
        (e: unknown) => {
          if (id !== reqIdRef.current) return
          const message = e instanceof Error && e.message ? e.message : ""
          dispatch(mode === "immediate" ? { type: "REVERT", message } : { type: "COMMIT_FAIL", message })
          onErrorRef.current?.(message)
        },
      )
    },
    [mode],
  )

  const setValue = useCallback(
    (next: T) => {
      dispatch({ type: "EDIT", value: next })
      if (mode === "immediate") commitValue(next)
    },
    [mode, commitValue],
  )

  // Set + persist inmediato con el valor explícito (no lee stateRef → sin lag de
  // sincronización). Para campos discretos dentro de un grupo en modo "blur".
  const saveNow = useCallback(
    (next: T) => {
      dispatch({ type: "EDIT", value: next })
      commitValue(next)
    },
    [commitValue],
  )

  const commit = useCallback(() => {
    commitValue(stateRef.current.value)
  }, [commitValue])

  const reset = useCallback((value: T) => {
    reqIdRef.current++ // cancela cualquier commit en vuelo
    dispatch({ type: "RESET", value })
  }, [])

  // "Guardado" → idle tras el linger.
  useEffect(() => {
    if (state.status !== "saved") return
    const h = setTimeout(() => dispatch({ type: "LINGER_DONE" }), savedLingerMs)
    return () => clearTimeout(h)
  }, [state.status, savedLingerMs])

  return {
    value: state.value,
    status: state.status,
    error: state.error,
    setValue,
    saveNow,
    commit,
    onBlur: commit,
    reset,
  }
}
